import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import InputAdornment from '@mui/material/InputAdornment';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import StableMuiDialog from '../../common/StableMuiDialog.js';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import SvgIconButton from './SvgIconButton';
import { toast } from '../../common/toast';
import debounce from 'lodash/debounce';
import { NumberFormatCustom } from './TableCustomToolbar';
import { useUser } from '../UserContext';
import { WEIGHT_MATRIX } from '../../common/mappings';
import { updateOfferWithDialog } from './SummaryCaseTable/services/simpleOfferUpdateWithDialog';
import OfferRoundConfirmationDialog from './SummaryCaseTable/components/OfferRoundConfirmationDialog.js';
import { usePersistedDataStore } from '../store';
import { createOfferContext } from '../../utils/offerContextUtils';
import { getConversionFactor } from '../../utils/conversionUtils';
import { getIFMVUtil, getAVUtil } from '../../utils/getAVUtil';
import { validateAndAdjustOfferValue } from '../../utils/offerThresholdUtils';
import { getMostRecentOfferValue, getMostRecentOfferRound } from '../../utils/mostRecentOfferUtils';
import { getCaseStrengthPercentage } from '../../utils/caseStrengthCalculator';
import logger from '../../utils/logger';
import OfferLadderRail, { getLadderFromCase, useOfferLadderCollapsed } from './OfferLadderRail';
import { formatPortfolioReduction, formatWinRateLine, resultTypeDisplayLabel } from './MunicipalResults/utils.js';

const TOOLBAR_OFFER_KIND = { AVENTINE: 'aventine', TOWN: 'town' };
const INITIAL_TOOLBAR_OFFER_DISPLAY_OVERRIDE = { offer: null, town: null };

const ToolbarValueDisplay = ({
  selectedKey,
  handleSelectChange,
  compAvg,
  pctAdj,
  ifmv,
  pctAdjClass,
  currentOfferValue,
  offerInputRef,
  debouncedOfferPct,
  reviewDetails,
  getUserNameById,
  view,
  getNegotiationObj,
  comp,
  townOfferValue,
  townOfferInputRef,
  isSettled = false,
}) => {
  const { user } = useUser();
  const isTrainingUser = user?.userAccess?.level === 'Training';
  const townOfferProdCheckbox = usePersistedDataStore((state) => state.townOfferProdCheckbox);
  const setTownOfferProdCheckbox = usePersistedDataStore((state) => state.setTownOfferProdCheckbox);

  // Get training offers for non-training users
  const trainingOffers = !isTrainingUser ? (getNegotiationObj?.cases?.[comp]?.TrainingOffers || []) : [];
  
  const currentCase = getNegotiationObj?.cases?.[comp];
  if (!currentCase) throw new Error('[ToolbarValueDisplay] Missing current case.');
  const eq = currentCase.EQ;
  const rar = currentCase.RAR;

  // Calculate conversion factor using the same logic as SummaryCaseTable
  const conversionFactor = useMemo(() => {
    return getConversionFactor(eq, rar);
  }, [eq, rar]);

  const compSheetForCase = usePersistedDataStore((state) => state.compSheet?.[comp]);
  const earliestCutoffDate = usePersistedDataStore((state) => state.negotiationObj?.earliestCutoffDate);
  const ladderAV = useMemo(() => getLadderFromCase(currentCase), [currentCase]);
  const settledPerformance = usePersistedDataStore((state) => state.settledPerformance);
  const settledPerformanceLoading = usePersistedDataStore((state) => state.settledPerformanceLoading);
  const [ladderCollapsed, toggleLadderCollapsed] = useOfferLadderCollapsed();

  useEffect(() => {
    const offers = {
      AlgorithmFirstOffer: currentCase.AlgorithmFirstOffer,
      AlgorithmSecondOffer: currentCase.AlgorithmSecondOffer,
      AlgorithmCeilingOffer: currentCase.AlgorithmCeilingOffer,
    };
    const liveCaseStrength = getCaseStrengthPercentage(comp, getNegotiationObj?.reportType || 'standard').caseStrengthA;
    const algoKeys = Object.keys(currentCase).filter((key) => /algorithm|ladder|ceiling/i.test(key));
    const hiddenBecause = ladderAV
      ? null
      : 'missing rung or offers are not in ascending order';
    logger.log('[OfferLadder]', {
      parcel_id: currentCase.parcel_id,
      offers,
      storedCaseStrength: currentCase.CaseStrength,
      liveCaseStrength,
      algoKeys,
      ladderAV,
      hiddenBecause,
    });
  }, [currentCase, comp, getNegotiationObj?.reportType, ladderAV, compSheetForCase, earliestCutoffDate]);

  const showOfferLadder = Boolean(ladderAV);

  // Square the top corners where the rail (or its collapsed tab) sits.
  const offerGroupCorners = !showOfferLadder
    ? 'rounded-md'
    : ladderCollapsed
      ? 'rounded-b-md rounded-tl-md'
      : 'rounded-b-md';

  const [isAVMode, setIsAVMode] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [townAnchorEl, setTownAnchorEl] = useState(null);
  const lastToastTimeRef = useRef(0);
  const isInitializingRef = useRef(true);
  
  // Offer round confirmation dialog state
  const [offerRoundDialogOpen, setOfferRoundDialogOpen] = useState(false);
  const [offerRoundDialogData, setOfferRoundDialogData] = useState(null);
  
  // Production checkbox confirmation dialog state
  const [prodCheckboxDialogOpen, setProdCheckboxDialogOpen] = useState(false);
  
  // Force re-render keys for input reversion
  const [offerInputKey, setOfferInputKey] = useState(0);
  const [townOfferInputKey, setTownOfferInputKey] = useState(0);
  // When validation adjusts to min/max, show that value until props catch up (AV terms)
  const [displayOverride, setDisplayOverride] = useState(INITIAL_TOOLBAR_OFFER_DISPLAY_OVERRIDE);





  // Reusable context creation function - DRY approach for all offer service calls
  const getOfferContext = useCallback(() => createOfferContext(user), [user]);
  // Helper function to generate weight tooltip content
  const getWeightTooltipContent = (compCount) => {
    if (!compCount || compCount < 1) return 'No comps selected';
    
    const weights = WEIGHT_MATRIX[Math.min(compCount, 10)];
    if (!weights) return 'Simple average (no weights)';
    
    return (
      <div>
        <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Weighted Average Weights:</div>
        {weights.map((weight, index) => (
          <div key={index} style={{ marginBottom: '2px' }}>
            Comp {index + 1}: {(weight * 100).toFixed(0)}%
          </div>
        ))}
      </div>
    );
  };

  // Helper function to get current value based on selectedKey
  const getCurrentValue = useCallback(() => {
    if (selectedKey === "selectedComps") return compAvg.selectedComps;
    if (selectedKey === "selectedMedian") return compAvg.selectedMedian;
    if (selectedKey === "top5") return compAvg.top5;
    return null;
  }, [selectedKey, compAvg]);

  // Helper function to check if value is valid
  const isValidValue = useCallback((value) => {
    return value !== null && value !== undefined && value !== 'None selected' && !isNaN(value);
  }, []);

  // Helper function to format MV value (rounds to nearest 10, except for median)
  const formatMVValue = useCallback((value, isMedian = false) => {
    if (!isValidValue(value)) return null;
    return isMedian ? value : Math.round(value / 10) * 10;
  }, [isValidValue]);

  // Helper function to get comp count for tooltip
  const getCompCount = useCallback(() => {
    if (selectedKey === "top5") return 5;
    return compAvg.selectedCompsCount || 0;
  }, [selectedKey, compAvg.selectedCompsCount]);

  // Shared TextField styles
  const textFieldStyles = {
    "& .MuiInputBase-root": {
      padding: "0px",
      height: "20px",
      width: "85px",
      minWidth: "85px",
      maxWidth: "120px",
      minHeight: "20px",
      maxHeight: "20px",
      border: "1px solid transparent",
      backgroundColor: "transparent",
      "&.Mui-disabled": {
        "& input": {
          WebkitTextFillColor: "#000 !important",
          color: "#000 !important",
          fontWeight: "bold",
        }
      },
    },
    "& input": {
      textAlign: "right",
      padding: "0px 2px",
      fontSize: '0.75rem',
      fontWeight: 'bold',
      height: "20px",
      minHeight: "20px",
      maxHeight: "20px",
      color: "#000 !important",
      WebkitTextFillColor: "#000 !important",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  };

  const emptyTextFieldStyles = {
    ...textFieldStyles,
    "&.Mui-disabled": {
      "& input": {
        WebkitTextFillColor: "#666 !important",
        color: "#666 !important",
      }
    },
    "& input": {
      ...textFieldStyles["& input"],
      color: "#666 !important",
      WebkitTextFillColor: "#666 !important",
    },
  };

  // Reusable component for rendering value display
  const renderValueDisplay = useCallback(() => {
    const currentValue = getCurrentValue();
    const isMedian = selectedKey === "selectedMedian";
    const isValid = isValidValue(currentValue);
    const formattedValue = formatMVValue(currentValue, isMedian);

    if (!isValid) {
      return (
        <div className='flex items-center ml-0 pr-1 pl-0'>
          <div className='flex' style={{ height: '34.5px', marginTop: '2px' }}>
            <div className='flex flex-col'>
              <TextField
                value="No Comps"
                variant="outlined"
                disabled
                sx={emptyTextFieldStyles}
              />
              <span className="text-[10px] text-right font-normal pr-1 text-gray-500" style={{ marginTop: '-4px', width: '85px', minWidth: '85px', maxWidth: '120px' }}>
                {isAVMode ? 'MV' : 'AV'}: None
              </span>
            </div>
          </div>
        </div>
      );
    }

    const compCount = getCompCount();
    const showTooltip = selectedKey !== "top5" || compCount > 0;
    const mvDisplay = formattedValue !== null ? formattedValue : currentValue;
    const avDisplay = Math.round(currentValue * conversionFactor);
    const displayValue = isAVMode ? avDisplay : mvDisplay;
    const valueInputProps = {
      startAdornment: (
        <InputAdornment
          position="end"
          className="relative flex items-center"
        >
          <div className="flex items-center relative">
            <span className="z-10 font-bold text-sm" style={{ color: isAVMode ? '#ff9800' : '#6b7280' }}>{isAVMode ? 'AV' : '$'}</span>
          </div>
        </InputAdornment>
      ),
      inputComponent: NumberFormatCustom,
    };

    return (
      <div className='flex items-center ml-0 pr-1 pl-0'>
        <div className='flex' style={{ height: '34.5px', marginTop: '2px' }}>
          <div className='flex flex-col'>
            {showTooltip ? (
              <Tooltip 
                title={getWeightTooltipContent(compCount)}
                placement="top"
                arrow
              >
                <TextField
                  value={displayValue.toLocaleString()}
                  variant="outlined"
                  disabled
                  InputProps={valueInputProps}
                  sx={textFieldStyles}
                />
              </Tooltip>
            ) : (
              <TextField
                value={displayValue.toLocaleString()}
                variant="outlined"
                disabled
                InputProps={valueInputProps}
                sx={textFieldStyles}
              />
            )}
            <span className="text-[10px] text-right font-normal pr-1" style={{ marginTop: '-4px', width: '85px', minWidth: '85px', maxWidth: '120px' }}>
              {isAVMode ? 'MV' : 'AV'}: {(isAVMode ? mvDisplay : avDisplay).toLocaleString()}
            </span>
          </div>
          {selectedKey !== "top5" && (
            <span className={`text-[9px] ml-0 flex items-center ${pctAdjClass}`}>
              ({Math.round(pctAdj * 100)}%)
            </span>
          )}
          {selectedKey === "top5" && pctAdj > -0.6 && (
            <span className={`text-[9px] ml-0 flex items-center ${pctAdjClass}`}>
              ({Math.round(pctAdj * 100)}%)
            </span>
          )}
        </div>
      </div>
    );
  }, [selectedKey, getCurrentValue, isValidValue, formatMVValue, getCompCount, getWeightTooltipContent, conversionFactor, pctAdj, pctAdjClass, isAVMode]);

  // Reset initialization flag and display override when comp changes
  React.useEffect(() => {
    isInitializingRef.current = true;
    setDisplayOverride(INITIAL_TOOLBAR_OFFER_DISPLAY_OVERRIDE);
    const timeout = setTimeout(() => {
      isInitializingRef.current = false;
    }, 500);
    return () => clearTimeout(timeout);
  }, [comp]);

  const effectiveOfferValue = displayOverride.offer ?? currentOfferValue;
  const effectiveTownOfferValue = displayOverride.town ?? townOfferValue;
  const prevCurrentOfferValueRef = useRef(currentOfferValue);
  const prevTownOfferValueRef = useRef(townOfferValue);
  React.useEffect(() => {
    const prevOffer = prevCurrentOfferValueRef.current;
    const prevTown = prevTownOfferValueRef.current;
    prevCurrentOfferValueRef.current = currentOfferValue;
    prevTownOfferValueRef.current = townOfferValue;
    setDisplayOverride((o) => {
      let offerNext = o.offer;
      let townNext = o.town;
      if (prevOffer !== currentOfferValue && o.offer != null && currentOfferValue != null
        && Math.abs(Number(o.offer) - Number(currentOfferValue)) <= 1) {
        offerNext = null;
      }
      if (prevTown !== townOfferValue && o.town != null && townOfferValue != null
        && Math.abs(Number(o.town) - Number(townOfferValue)) <= 1) {
        townNext = null;
      }
      if (offerNext === o.offer && townNext === o.town) return o;
      return { offer: offerNext, town: townNext };
    });
  }, [currentOfferValue, townOfferValue]);

  const aventineRound = getMostRecentOfferRound(currentCase?.Negotiations, 'Aventine');
  const townRound = getMostRecentOfferRound(currentCase?.Negotiations, 'Town');
  
  // Check if town offer is from a prior round
  const isTownOfferFromPriorRound = townRound !== null && aventineRound !== null && townRound < aventineRound;
  
  // Check if aventine offer is from a prior round
  const isAventineOfferFromPriorRound = aventineRound !== null && townRound !== null && aventineRound < townRound;


  // Simple dialog handlers
  const handleDialogClose = useCallback(() => {
    setOfferRoundDialogOpen(false);
    setOfferRoundDialogData(null);
  }, []);

  const handleShowDialog = useCallback((dialogData) => {
    // Wrap callbacks to close dialog after execution
    const dialogDataWithClose = {
      ...dialogData,
      onConfirm: () => {
        dialogData.onConfirm?.();
        handleDialogClose();
      },
      onCancel: () => {
        dialogData.onCancel?.();
        handleDialogClose();
      },
      onEditPriorRound: (roundNumber) => {
        dialogData.onEditPriorRound?.(roundNumber);
        handleDialogClose();
      }
    };
    setOfferRoundDialogData(dialogDataWithClose);
    setOfferRoundDialogOpen(true);
  }, [handleDialogClose]);

  // Debounced version of handleShowDialog to prevent showing dialog while user is typing
  const debouncedShowDialog = useRef(
    debounce((dialogData) => {
      handleShowDialog(dialogData);
    }, 2000) // 2 second debounce to match the offer update debounce
  ).current;

  // Debounced toast function for offer validation (maxAllowedValue in MV; show in AV or $ per isAVMode)
  const debouncedOfferToastRef = useRef(
    debounce((maxAllowedValueMV, isAVMode, conversionFactor) => {
      const now = Date.now();
      if (now - lastToastTimeRef.current > 4000) {
        lastToastTimeRef.current = now;
        const displayValue = isAVMode ? Math.round(maxAllowedValueMV * conversionFactor) : maxAllowedValueMV;
        const label = isAVMode ? `AV ${displayValue.toLocaleString()}` : `$${displayValue.toLocaleString()}`;
        toast.info(`User input offer > 25% reduction, ${label} is max reduction`, {
          autoClose: 7000
        });
      }
    }, 2000) // 2 second debounce
  );

  // Single debounced path: same validation (incl. isAVMode) for Aventine + Town toolbar offers
  const debouncedToolbarOfferUpdateRef = useRef(
    debounce((updateData) => {
      const {
        kind,
        valueToSend: initialValue,
        context,
        row,
        isAVMode,
        parcelId,
      } = updateData;

      const prodTown = usePersistedDataStore.getState().townOfferProdCheckbox;
      const contextForRequest = kind === TOOLBAR_OFFER_KIND.TOWN
        ? {
            ...context,
            userAccess: {
              ...context.userAccess,
              level: prodTown ? 'Aventine' : context.userAccess.level
            }
          }
        : context;

      let valueToSend = initialValue;

      if (valueToSend == null || valueToSend <= 0) {
        debouncedOfferToastRef.current.cancel();
        if (kind === TOOLBAR_OFFER_KIND.AVENTINE) {
          if (valueToSend === 0 && offerInputRef?.current) {
            offerInputRef.current.getValue = () => null;
          }
          return;
        }
        if (valueToSend === 0 && townOfferInputRef?.current) {
          townOfferInputRef.current.getValue = () => '';
        }
        if (valueToSend != null && valueToSend <= 0) {
          return;
        }
        updateOfferWithDialog(
          parcelId,
          { TownOffer: null, OfferedBy: 'Town' },
          contextForRequest,
          { updateFrontend: true },
          () => {},
          () => {},
          debouncedShowDialog
        );
        return;
      }

      const validationResult = validateAndAdjustOfferValue(
        valueToSend,
        row,
        isAVMode,
        true,
        { inputInAVTerms: true }
      );
      logger.log('🔍 validationResult', validationResult);

      if (validationResult.wasAdjusted) {
        const adjustedAV = validationResult.adjustedValueAV ?? validationResult.adjustedValue;
        const key = kind === TOOLBAR_OFFER_KIND.AVENTINE ? 'offer' : 'town';
        setDisplayOverride((o) => ({ ...o, [key]: adjustedAV }));
        if (kind === TOOLBAR_OFFER_KIND.AVENTINE) {
          setOfferInputKey((k) => k + 1);
          if (offerInputRef?.current) {
            offerInputRef.current.getValue = () => String(Math.round(adjustedAV));
          }
        } else {
          setTownOfferInputKey((k) => k + 1);
          if (townOfferInputRef?.current) {
            townOfferInputRef.current.getValue = () => String(Math.round(adjustedAV));
          }
        }
        valueToSend = adjustedAV;
      } else {
        debouncedOfferToastRef.current.cancel();
      }

      const offerPayload = kind === TOOLBAR_OFFER_KIND.AVENTINE
        ? { OfferValue: valueToSend }
        : { TownOffer: valueToSend, OfferedBy: 'Town' };

      updateOfferWithDialog(
        parcelId,
        offerPayload,
        contextForRequest,
        { updateFrontend: true },
        () => {},
        () => {},
        debouncedShowDialog
      );
    }, 2000)
  ).current;

  const handleOfferValueChangeWithMode = useCallback((e) => {
    
    // Skip if this is a programmatic change during comp switch
    if (isInitializingRef.current) {
      return;
    }
    
    // Skip if this is not a real user input event (e.g., programmatic value change)
    if (!e || !e.target) {
      return;
    }
    // Early returns below must not let a prior keystroke's pending update fire
    debouncedToolbarOfferUpdateRef.cancel();

    // // Check if case is settled - inputs should be disabled, so this shouldn't be reached
    // if (isSettled) {
    //   logger.log('🔍 Case is settled, input should be disabled');
    //   return;
    // }
    
    const value = e.target.value;
    let finalValue = value;
    
    
    // Handle the case where NumericFormat might return '0' for empty input
    if (value === '0' || value === '') {
      finalValue = null;
    }
    
    if (isAVMode) {
      // If in AV mode, the input is already in AV terms, so use it directly
      finalValue = value;
    } else {
      // If not in AV mode, convert the input MV value to AV for storage
      const avValue = value ? Math.round(parseFloat(value.replace(/,/g, '')) * conversionFactor).toString() : '';
      finalValue = avValue;
    }
    
    if (offerInputRef?.current) {
      offerInputRef.current.getValue = () => finalValue;
    }
    
    // Handle service call with proper normalization
    const context = getOfferContext();
    
    // For non-empty values, convert to number; for empty values, pass null
    let valueToSend = (finalValue !== null && finalValue !== undefined && finalValue !== '') 
      ? parseFloat(finalValue.toString().replace(/,/g, ''))
      : null;
    
    // Skip service calls during component initialization (when comp changes)
    if (isInitializingRef.current) {
      logger.log('🔍 handleOfferValueChange - Skipping service call - component initializing');
      return;
    }
    
    const mostRecentAventineOffer = getMostRecentOfferValue(currentCase?.Negotiations, 'Aventine');
    const currentStoredValue = mostRecentAventineOffer !== null ? mostRecentAventineOffer : (currentCase?.OfferValue || null);
    
    
        // Normalize both values for comparison (treat undefined as null)
        const normalizedValueToSend = valueToSend === undefined ? null : valueToSend;
        const normalizedStoredValue = currentStoredValue === undefined ? null : currentStoredValue;
        
        // Check for exact match first
        const isExactMatch = normalizedValueToSend === normalizedStoredValue;
        
        // Check for tolerance match (within $1 for AV/MV conversion differences)
        const isWithinTolerance = normalizedValueToSend !== null && normalizedStoredValue !== null && 
          Math.abs(normalizedValueToSend - normalizedStoredValue) <= 1;
        
        const isValueDifferent = !isExactMatch && !isWithinTolerance;
        
        if (!isValueDifferent) {
          logger.log('🔍 [AV MODE DEBUG] Skipping service call - value unchanged (exact match or within tolerance)');
          return;
        }
    
    if (valueToSend == null || valueToSend <= 0) {
      debouncedOfferToastRef.current.cancel();
      if (valueToSend === 0 && offerInputRef?.current) {
        offerInputRef.current.getValue = () => null;
      }
      return;
    }

    debouncedToolbarOfferUpdateRef({
      kind: TOOLBAR_OFFER_KIND.AVENTINE,
      valueToSend,
      context,
      row: currentCase,
      isAVMode,
      parcelId: currentCase?.parcel_id,
    });
  }, [isAVMode, conversionFactor, offerInputRef, currentCase, debouncedToolbarOfferUpdateRef, debouncedOfferToastRef, getOfferContext]);


  // Why tf would you have two of these functions hahahah
  // This is the problematic function, it triggers more than just onchange.
  const handleTownOfferValueChangeWithMode = useCallback((e) => {
    // Skip if this is a programmatic change during comp switch
    if (isInitializingRef.current) {
      return;
    }
    
    // Skip if this is not a real user input event (e.g., programmatic value change)
    if (!e || !e.target) {
      return;
    }
    debouncedToolbarOfferUpdateRef.cancel();

    // if (isSettled) {
    //   return;
    // }
    
    const value = e.target.value;
    let finalValue = value;
    if (value === '0' || value === '') {
      finalValue = null;
    }
    
    if (isAVMode) {
      // If in AV mode, the input is already in AV terms, so use it directly
      finalValue = value;
    } else {
      // If not in AV mode, convert the input MV value to AV for storage
      const avValue = value ? Math.round(parseFloat(value.replace(/,/g, '')) * conversionFactor).toString() : '';
      finalValue = avValue;
    }
    
    if (townOfferInputRef?.current) {
      townOfferInputRef.current.getValue = () => finalValue;
    }
    
    // Handle service call with proper normalization
    const context = getOfferContext();
    
    // For non-empty values, convert to number; for empty values, pass null
    let valueToSend = (finalValue !== null && finalValue !== undefined && finalValue !== '') 
      ? parseFloat(finalValue.toString().replace(/,/g, ''))
      : null;
    
    
    const mostRecentTownOffer = getMostRecentOfferValue(currentCase?.Negotiations, 'Town');
    const currentStoredTownOffer = mostRecentTownOffer !== null ? mostRecentTownOffer : (currentCase?.TownOffer || null);
    
    // Normalize both values for comparison (treat undefined as null)
    const normalizedValueToSend = valueToSend === undefined ? null : valueToSend;
    const normalizedStoredValue = currentStoredTownOffer === undefined ? null : currentStoredTownOffer;
    
    // Check for exact match first
    const isExactMatch = normalizedValueToSend === normalizedStoredValue;
    
    // Check for tolerance match (within $1 for AV/MV conversion differences)
    const isWithinTolerance = normalizedValueToSend !== null && normalizedStoredValue !== null && 
      Math.abs(normalizedValueToSend - normalizedStoredValue) <= 1;
    
    const isTownOfferDifferent = !isExactMatch && !isWithinTolerance;
    
    if (!isTownOfferDifferent) {
      return;
    }
    
    if (valueToSend === null || (!isNaN(valueToSend) && valueToSend > 0)) {
      debouncedToolbarOfferUpdateRef({
        kind: TOOLBAR_OFFER_KIND.TOWN,
        valueToSend,
        context,
        row: currentCase,
        isAVMode,
        parcelId: currentCase?.parcel_id,
      });
    } else if (valueToSend === 0) {
      if (townOfferInputRef?.current) {
        townOfferInputRef.current.getValue = () => '';
      }
    }
  }, [isAVMode, currentCase, townOfferInputRef, debouncedToolbarOfferUpdateRef, getOfferContext, conversionFactor]);

  // Helper to safely parse numeric value
  const parseNumericValue = (value) => {
    if (!value || value === '') return null;
    const parsed = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
    return isNaN(parsed) ? null : parsed;
  };

  const handleSetMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSetMenuClose = () => {
    setAnchorEl(null);
  };

  const handleTownSetMenuOpen = (event) => {
    setTownAnchorEl(event.currentTarget);
  };

  const handleTownSetMenuClose = () => {
    setTownAnchorEl(null);
  };

  const handleProdCheckboxChange = (event) => {
    if (event.target.checked) {
      // Show confirmation dialog when trying to enable
      setProdCheckboxDialogOpen(true);
    } else {
      // Allow disabling without confirmation
      setTownOfferProdCheckbox(false);
    }
  };

  const handleProdCheckboxConfirm = () => {
    setTownOfferProdCheckbox(true);
    setProdCheckboxDialogOpen(false);
  };

  const handleProdCheckboxCancel = () => {
    setProdCheckboxDialogOpen(false);
  };



  const handleSetMaxOffer = () => {
    const maxAllowedValue = Math.round(ifmv * 0.75);
    // Convert MV to AV for storage (values are stored in AV terms)
    const valueToSet = maxAllowedValue * conversionFactor;
    
    if (offerInputRef?.current) {
      offerInputRef.current.getValue = () => valueToSet.toString();
    }
    
    // Handle service call immediately (no debounce for Set buttons)
    const context = createOfferContext(user, null, 'Manual');
    
    const valueToSend = parseFloat(valueToSet.toString().replace(/,/g, ''));
    updateOfferWithDialog(
      getNegotiationObj.cases[comp].parcel_id,
      { OfferValue: valueToSend },
      context,
      { updateFrontend: true },
      (result) => {
        logger.log('✅ Offer value updated successfully:', result);
      },
      (error) => {
        logger.error('❌ Failed to update offer value:', error);
      },
      handleShowDialog
    );
    
    handleSetMenuClose();
  };

  const handleSetNoChangeTownOffer = () => {
    // Set to IFMV rounded to nearest integer
    const roundedIFMV = Math.round(ifmv);
    // Convert MV to AV for storage (values are stored in AV terms)
    const valueToSet = roundedIFMV * conversionFactor;
    
    if (townOfferInputRef?.current) {
      townOfferInputRef.current.getValue = () => valueToSet.toString();
    }
    
    const context = createOfferContext(user, null, 'Manual');
    const valueToSend = parseFloat(valueToSet.toString().replace(/,/g, ''));
    const caseRow = getNegotiationObj.cases[comp];

    debouncedToolbarOfferUpdateRef({
      kind: TOOLBAR_OFFER_KIND.TOWN,
      valueToSend,
      context,
      row: caseRow,
      isAVMode,
      parcelId: caseRow.parcel_id,
    });
    
    handleTownSetMenuClose();
  };

  const handleAcceptTrainingOffer = (trainingOffer) => {
    // Convert training offer AV value to MV for display and storage
    const offerValue = trainingOffer.Offer || 0;
    const mvValue = Math.round(offerValue / conversionFactor / 10) * 10;
    const avValue = mvValue * conversionFactor;
    
    if (offerInputRef?.current) {
      offerInputRef.current.getValue = () => avValue.toString();
    }
    
    // Handle service call immediately (no debounce for Accept button)
    const context = createOfferContext(user, null, 'Manual');
    
    const valueToSend = parseFloat(avValue.toString().replace(/,/g, ''));
    updateOfferWithDialog(
      getNegotiationObj.cases[comp].parcel_id,
      { OfferValue: valueToSend },
      context,
      { updateFrontend: true },
      (result) => {
        logger.log('✅ Training offer accepted and Aventine offer updated successfully:', result);
        toast.success(`Training offer accepted! Aventine offer set to $${mvValue.toLocaleString()}`, {
          autoClose: 3000
        });
      },
      (error) => {
        logger.error('❌ Failed to accept training offer:', error);
        toast.error('Failed to accept training offer. Please try again.', {
          autoClose: 3000
        });
      },
      handleShowDialog
    );
  };

  const handleSetRandomOffer = () => {
    // Generate random percentage between 94-97% (3-6% reduction)
    const randomPercentage = Math.random() * 3 + 94; // 94 to 97
    const randomValue = Math.round(ifmv * (randomPercentage / 100));
    
    // Convert MV to AV for storage (values are stored in AV terms)
    const avValue = randomValue * conversionFactor;
    const valueToSet = Math.round(avValue / 50) * 50; // Round to nearest 50
    
    if (offerInputRef?.current) {
      offerInputRef.current.getValue = () => valueToSet.toString();
    }
    
    // Handle service call immediately (no debounce for Set buttons)
    const context = createOfferContext(user, null, 'Random');
    
    const valueToSend = parseFloat(valueToSet.toString().replace(/,/g, ''));
    updateOfferWithDialog(
      getNegotiationObj.cases[comp].parcel_id,
      { OfferValue: valueToSend },
      context,
      { updateFrontend: true },
      (result) => {
        logger.log('✅ Offer value updated successfully:', result);
      },
      (error) => {
        logger.error('❌ Failed to update offer value:', error);
      },
      handleShowDialog
    );
    
    handleSetMenuClose();
  };

  const handleSetNoChangeOffer = () => {
    // Set to IFMV rounded to nearest integer
    const roundedIFMV = Math.round(ifmv);
    // Convert MV to AV for storage (values are stored in AV terms)
    const valueToSet = roundedIFMV * conversionFactor;
    logger.log('🔍 handleSetNoChangeOffer - valueToSet:', valueToSet);
    
    if (offerInputRef?.current) {
      offerInputRef.current.getValue = () => valueToSet.toString();
    }
    
    // Handle service call immediately (no debounce for Set buttons)
    const context = createOfferContext(user, null, 'Manual');
    
    const valueToSend = parseFloat(valueToSet.toString().replace(/,/g, ''));
    updateOfferWithDialog(
      getNegotiationObj.cases[comp].parcel_id,
      { OfferValue: valueToSend },
      context,
      { updateFrontend: true },
      (result) => {
        logger.log('✅ Offer value updated successfully:', result);
      },
      (error) => {
        logger.error('❌ Failed to update offer value:', error);
      },
      handleShowDialog
    );
    
    handleSetMenuClose();
  };

  // Helper function to apply unified bounding logic and set offer value
  const setOfferWithBounding = useCallback((value, source = 'Workup') => {
    if (!isValidValue(value)) {
      return false;
    }
    
    const workupValue = Math.round(value);

    const validationResult = validateAndAdjustOfferValue(
      workupValue,
      currentCase,
      false,
      true
    );

    const boundedMV = validationResult.adjustedValue;
    // Convert MV to AV for storage (values are stored in AV terms)
    const valueToSet = boundedMV * conversionFactor;
    
    if (offerInputRef?.current) {
      offerInputRef.current.getValue = () => valueToSet.toString();
    }
    
    // Handle service call immediately (no debounce for Set buttons)
    const context = createOfferContext(user, null, source);
    
    const valueToSend = parseFloat(valueToSet.toString().replace(/,/g, ''));
    updateOfferWithDialog(
      getNegotiationObj.cases[comp].parcel_id,
      { OfferValue: valueToSend },
      context,
      { updateFrontend: true },
      (result) => {
        logger.log('✅ Offer value updated successfully:', result);
      },
      (error) => {
        logger.error('❌ Failed to update offer value:', error);
      },
      debouncedShowDialog
    );
    
    return true;
  }, [conversionFactor, offerInputRef, getNegotiationObj, comp, currentCase, user, debouncedShowDialog, isValidValue]);

  const handleSelectLadderRung = useCallback((rungKey, rungMV) => {
    setOfferWithBounding(rungMV, 'Algo');
  }, [setOfferWithBounding]);

  const handleSetWorkupAverageOffer = () => {
    const currentValue = getCurrentValue();
    if (setOfferWithBounding(currentValue)) {
      handleSetMenuClose();
    }
  };

  const handleSetSelectedMedianOffer = () => {
    const medianValue = compAvg.selectedMedian;
    if (setOfferWithBounding(medianValue)) {
      handleSetMenuClose();
    }
  };

  // 25% reduction warning is now handled centrally by validateAndAdjustOfferValue

  const priorAvg = settledPerformance?.priorAvg;
  const currentSettledRatio = settledPerformance?.ratio;
  const priorDeltaPct = priorAvg == null || currentSettledRatio == null
    ? null
    : Math.round((priorAvg - currentSettledRatio) * 1000) / 10;
  const priorYears = [...(settledPerformance?.priorYears ?? [])].sort((a, b) => b.year - a.year);
  const abovePrior = priorDeltaPct > 0;
  const belowPrior = priorDeltaPct < 0;

  return (
    <div className="flex relative font-bold text-sm items-center justify-center flex-grow">
      <div className="flex items-center gap-1.5">
        <div className="flex items-center border border-[#cfd5dd] bg-white pr-1 pl-1 rounded-md">
          <div className='flex flex-col' style={{ marginTop: '2px' }}>
            <FormControl
              variant="standard"
              sx={{
                minWidth: 65,
                '& .MuiInput-underline:before': {
                  borderBottom: 'none',
                },
                '& .MuiInput-underline:hover:before': {
                  borderBottom: 'none',
                },
                '& .MuiInput-underline:after': {
                  borderBottom: 'none',
                },
              }}
            >
              <Select
                labelId="comp-avg-select-label"
                value={selectedKey}
                onChange={handleSelectChange}
                disabled={view === 'negotiation' || view === 'town'}
                renderValue={(selected) => {
                  if (selected === "selectedComps" && isNaN(compAvg.selectedComps)) {
                    return "Select Comps";
                  }
                  if (selected === "selectedComps") {
                    return "Selected Avg";
                  }
                  if (selected === "selectedMedian") {
                    return "Selected Median";
                  }
                  if (selected === "top5") {
                    return "Top Five Avg";
                  }
                  return selected;
                }}
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 'bold',
                  '& .MuiSelect-select': {
                    padding: '0px 2px',
                  },
                }}
              >
                <MenuItem value="selectedComps">Selected Avg</MenuItem>
                <MenuItem value="selectedMedian">Selected Median</MenuItem>
                <MenuItem value="top5">Top Five Avg</MenuItem>
              </Select>
            </FormControl>
            <span className="text-[11px] font-normal flex items-center whitespace-nowrap">
              <Tooltip
                title={
                  <div>
                    <div style={{ 
                      color: reviewDetails.manualReview ? '#ff9800' : 'inherit' 
                    }}>
                      Manual Review: {reviewDetails.manualReview ? 'Yes' : 'No'}
                    </div>
                    {reviewDetails.manualReview && reviewDetails.reviewedBy && (
                      <div>Reviewed By: {getUserNameById(reviewDetails.reviewedBy)}</div>
                    )}
                  </div>
                }
                placement="bottom"
              >
                <InfoOutlinedIcon 
                  sx={{ 
                    fontSize: '12px', 
                    marginRight: '2px',
                    pointerEvents: 'auto',
                    cursor: 'pointer',
                    color: reviewDetails.manualReview ? '#ff9800' : '#666'
                  }} 
                />
              </Tooltip>
              Saved:{" "}
              {(() => {
                if (!reviewDetails.lastReviewDate) {
                  return 'Never';
                }
                
                const date = new Date(reviewDetails.lastReviewDate);
                
                if (isNaN(date.getTime())) {
                  return 'Never';
                }
                
                return new Date(date.getTime() + 46400000).toLocaleDateString(
                  "en-US",
                  {
                    year: "2-digit",
                    month: "2-digit",
                    day: "2-digit",
                  }
                );
              })()}
            </span>
          </div>
          {renderValueDisplay()}
        </div>

        <div className={`relative flex items-center border border-[#cfd5dd] bg-white pr-1 ${offerGroupCorners}`}>
          {showOfferLadder && (
            <OfferLadderRail
              ladderAV={ladderAV}
              ifmv={ifmv}
              conversionFactor={conversionFactor}
              ourOfferAV={effectiveOfferValue}
              townOfferAV={effectiveTownOfferValue}
              onSelect={handleSelectLadderRung}
              disabled={isSettled}
              collapsed={ladderCollapsed}
              onToggleCollapsed={toggleLadderCollapsed}
              isAVMode={isAVMode}
              zIndex={100}
            />
          )}
          <div className="flex items-center ml-0 pr-1 pl-1 lg:border-r border-[#e3e7ec]">
              <div className='flex flex-col' style={{ height: '34.5px', marginTop: '2px' }}>
                <div className="flex items-center">
                  <span className="text-[10px] mr-1 whitespace-nowrap">Our Offer</span>
                  <Tooltip 
                    title={
                      isSettled 
                        ? "Case is settled, cancel settlement to update" 
                        : isAventineOfferFromPriorRound 
                          ? `Aventine offer is from Round ${aventineRound}, but Town's most recent offer is from Round ${townRound}` 
                          : ""
                    }
                    placement="top"
                    disableHoverListener={!isSettled && !isAventineOfferFromPriorRound}
                  >
                    <TextField
                      key={`${getNegotiationObj?.cases?.[comp]?.parcel_id}:${isAVMode ? 'AV' : 'MV'}:${offerInputKey}`}
                      value={(() => {
                        const source = effectiveOfferValue;
                        if (!source) return '';
                        const v = parseNumericValue(source);
                        if (v == null) return '';
                        const propertyAssessmentForCase = getAVUtil(currentCase);
                        const equalsAssessment = propertyAssessmentForCase && Math.abs((v || 0) - propertyAssessmentForCase) <= 1;
                        const raw = isAVMode
                          ? Math.round(v)
                          : (equalsAssessment
                            ? Math.round(v / conversionFactor) // exact dollars when AV equals assessment
                            : Math.round(v / conversionFactor / 10) * 10); // round to nearest 10 otherwise
                        return String(raw);
                      })()}
                      onChange={handleOfferValueChangeWithMode}
                      variant="outlined"
                      placeholder={currentOfferValue ? "Offer" : "Set offer"}
                      inputRef={offerInputRef}
                      disabled={isSettled}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment
                            position="end"
                            className="relative flex items-center"
                          >
                            <div className="flex items-center relative">
                              <span className="z-10 font-bold text-sm" style={{ color: isAVMode ? '#ff9800' : 'inherit' }}>{isAVMode ? 'AV' : '$'}</span>
                            </div>
                          </InputAdornment>
                        ),
                        endAdornment: isAventineOfferFromPriorRound ? (
                          <InputAdornment position="end">
                            <InfoOutlinedIcon sx={{ fontSize: '14px', color: '#999', cursor: 'pointer' }} />
                          </InputAdornment>
                        ) : null,
                        inputComponent: NumberFormatCustom,
                      }}
                      autoComplete="off"
                      sx={{
                        "& .MuiInputBase-root": {
                          padding: "0px",
                          height: "20px",
                          width: "120px",
                          minWidth: "120px",
                          maxWidth: "120px",
                          minHeight: "20px",
                          maxHeight: "20px",
                          border: currentOfferValue ? (isAVMode ? "1px solid #ff9800" : "1px solid #98a2b3") : "1px solid #FF0000",
                          transition: "border-color 0.2s ease",
                          "&:hover": {
                            borderColor: currentOfferValue ? (isAVMode ? "#ff9800" : "#667085") : "#FF0000",
                          },
                        },
                        "& input": {
                          textAlign: "right",
                          padding: "0px 2px",
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          height: "20px",
                          minHeight: "20px",
                          maxHeight: "20px",
                          color: currentOfferValue ? (isAventineOfferFromPriorRound ? "#999" : "inherit") : "#666",
                          "&::placeholder": {
                            color: "#666",
                            opacity: 1
                          }
                        },
                      }}
                    />
                  </Tooltip>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <span className={`text-[9px] ${debouncedOfferPct < -0.0001 ? 'text-green-700' : 'text-red-700'} ${currentOfferValue === '' ? 'invisible' : ''}`} style={{ marginTop: '-4px' }}>
                      {(() => {
                        if (currentOfferValue === '') return '(0%)';
                        const pct = Math.round(debouncedOfferPct * 100);
                        return `(${pct}%)`;
                      })()}
                    </span>
                    {/* Training offers indicator for non-training users */}
                    {!isTrainingUser && trainingOffers.length > 0 && (
                      <Tooltip 
                        title={
                          <div>
                            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Training Offers:</div>
                            {trainingOffers.map((offer, index) => {
                              // Convert AV to MV for display
                              const offerValue = offer.Offer || 0;
                              const mvValue = Math.round(offerValue / conversionFactor / 10) * 10;
                              return (
                                <div key={index} style={{ marginBottom: '8px', padding: '4px', border: '1px solid #ddd', borderRadius: '4px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                    <div>
                                      <div>Offer: ${mvValue.toLocaleString()}</div>
                                      <div>By: {getUserNameById(offer.Negotiator)}</div>
                                      {offer.Comments && <div>Comments: {offer.Comments}</div>}
                                    </div>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleAcceptTrainingOffer(offer);
                                      }}
                                      style={{
                                        backgroundColor: '#4CAF50',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '4px 8px',
                                        fontSize: '10px',
                                        fontWeight: 'bold',
                                        cursor: 'pointer',
                                        marginLeft: '8px'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.target.style.backgroundColor = '#45a049';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.target.style.backgroundColor = '#4CAF50';
                                      }}
                                    >
                                      Accept
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        }
                        placement="top"
                        arrow
                        disableInteractive={false}
                      >
                        <span 
                          className="text-[10px] font-bold ml-1 cursor-pointer"
                          style={{ 
                            color: '#ff9800',
                            backgroundColor: '#fff3cd',
                            border: '1px solid #ff9800',
                            borderRadius: '50%',
                            width: '14px',
                            height: '14px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            lineHeight: '1',
                            marginTop: '-4px'
                          }}
                        >
                          T
                        </span>
                      </Tooltip>
                    )}
                  </div>
                  <div className="flex items-center">
                    <span className="text-[10px] text-right font-normal pr-1 flex items-center justify-end" style={{ marginTop: '-4px', width: '120px', minWidth: '120px', maxWidth: '120px' }}>
                      <SvgIconButton
                        icon={AutorenewIcon}
                        onClick={() => setIsAVMode((mode) => !mode)}
                        disabled={isSettled}
                        tooltip={isSettled ? "Case is Settled" : (isAVMode ? "Switch Offer to Market Value" : "Switch Offer to AV")}
                        className="p-0.5 mr-0.5"
                        size="small"
                        activeColor="text.secondary"
                        iconProps={{
                          sx: { fontSize: '12px' }
                        }}
                      />
                      <Tooltip title={isSettled ? "Case is Settled" : "Set offer options"} disableInteractive>
                        <IconButton
                          size="small"
                          onClick={handleSetMenuOpen}
                          disabled={isSettled}
                          sx={{ 
                            padding: '0px 2px', 
                            marginTop: '2px',
                            minWidth: 'auto',
                            width: 'auto',
                            height: '12px',
                            border: '1px solid #666',
                            borderRadius: '2px',
                            backgroundColor: 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <span style={{ fontSize: '8px', fontWeight: 'bold', lineHeight: '1' }}>SET</span>
                        </IconButton>
                      </Tooltip>
                      <span className="flex-grow text-right">
                        {isAVMode ? 'MV' : 'AV'}: {parseNumericValue(currentOfferValue) !== null ? (isAVMode ? (() => {
                          const v = parseNumericValue(currentOfferValue);
                          const propertyAssessmentForCase = (currentCase?.PropertyAssessment || currentCase?.AssessmentVillage || 0);
                          const equalsAssessment = Math.abs((v || 0) - propertyAssessmentForCase) <= 1;
                          return (equalsAssessment ? Math.round(v / conversionFactor) : Math.round(v / conversionFactor / 10) * 10).toLocaleString();
                        })() : Math.round(parseNumericValue(currentOfferValue)).toLocaleString()) : 'None'}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center ml-0 relative pl-1 hidden lg:flex">
              <div className='flex flex-col' style={{ height: '34.5px', marginTop: '2px' }}>
                <div className="flex items-center">
                  <span className="text-[10px] mr-1 whitespace-nowrap">Town Offer</span>
                  <Tooltip 
                    title={
                      isSettled 
                        ? "Case is settled, cancel settlement to update" 
                        : isTownOfferFromPriorRound 
                          ? `Town offer is from Round ${townRound}, but Aventine's most recent offer is from Round ${aventineRound}` 
                          : ""
                    }
                    placement="top"
                    disableHoverListener={!isSettled && !isTownOfferFromPriorRound}
                  >
                    <TextField
                      key={`${getNegotiationObj?.cases?.[comp]?.parcel_id}:${isAVMode ? 'AV' : 'MV'}:${townOfferInputKey}`}
                      value={(() => {
                        if (!effectiveTownOfferValue) return '';
                        const v = parseNumericValue(effectiveTownOfferValue);
                        if (v == null) return '';
                        // Now that we store AV values, convert AV to MV for display when not in AV mode
                        const propertyAssessmentForCase = (currentCase?.PropertyAssessment || currentCase?.AssessmentVillage || 0);
                        const equalsAssessment = Math.abs((v || 0) - propertyAssessmentForCase) <= 1;
                        const raw = isAVMode
                          ? Math.round(v)
                          : (equalsAssessment
                            ? Math.round(v / conversionFactor) // exact dollars when AV equals assessment
                            : Math.round(v / conversionFactor / 10) * 10); // round to nearest 10 otherwise
                        return String(raw);
                      })()}
                      onChange={handleTownOfferValueChangeWithMode}
                      variant="outlined"
                      placeholder={effectiveTownOfferValue ? "Town Offer" : "Set town offer"}
                      inputRef={townOfferInputRef}
                      disabled={isSettled}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment
                            position="end"
                            className="relative flex items-center"
                          >
                            <div className="flex items-center relative">
                              <span className="z-10 font-bold text-sm" style={{ color: isAVMode ? '#ff9800' : '#6b7280' }}>{isAVMode ? 'AV' : '$'}</span>
                            </div>
                          </InputAdornment>
                        ),
                        endAdornment: isTownOfferFromPriorRound ? (
                          <InputAdornment position="end">
                            <InfoOutlinedIcon sx={{ fontSize: '14px', color: '#999', cursor: 'pointer' }} />
                          </InputAdornment>
                        ) : null,
                        inputComponent: NumberFormatCustom,
                      }}
                      autoComplete="off"
                      sx={{
                        "& .MuiInputBase-root": {
                          padding: "0px",
                          height: "20px",
                          width: "120px",
                          minWidth: "120px",
                          maxWidth: "120px",
                          minHeight: "20px",
                          maxHeight: "20px",
                          border: isAVMode ? "1px solid #ff9800" : "1px solid transparent",
                          transition: "border-color 0.2s ease",
                          "&:hover": {
                            borderColor: isAVMode ? "#ff9800" : "#667085",
                          },
                        },
                        "& input": {
                          textAlign: "right",
                          padding: "0px 2px",
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          height: "20px",
                          color: effectiveTownOfferValue ? (isTownOfferFromPriorRound ? "#999" : "inherit") : "#666",
                          "&::placeholder": {
                            color: "#666",
                            opacity: 1
                          }
                        },
                      }}
                    />
                  </Tooltip>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <span className={`text-[9px] ${parseNumericValue(effectiveTownOfferValue) !== null ? (() => {
                      // Always convert AV value to MV for percentage calculation (stored values are in AV terms)
                      let townValue = parseNumericValue(effectiveTownOfferValue);
                      townValue = townValue / conversionFactor;
                      const ifmvValue = getIFMVUtil(getNegotiationObj?.cases?.[comp]);
                      if (ifmvValue == null) {
                        return 'invisible';
                      }
                      const diff = townValue - ifmvValue;
                      if (Math.abs(diff) < ifmvValue * 0.001) {
                        return 'invisible';
                      }
                      return diff < 0 ? 'text-green-700' : 'text-red-700';
                    })() : 'invisible'}`} style={{ marginTop: '-4px' }}>
                      {parseNumericValue(effectiveTownOfferValue) !== null ? (() => {
                        // Always convert AV value to MV for percentage calculation (stored values are in AV terms)
                        let townValue = parseNumericValue(effectiveTownOfferValue);
                        townValue = townValue / conversionFactor;
                        const ifmvValue = getIFMVUtil(getNegotiationObj?.cases?.[comp]);
                        if (!ifmvValue) {
                          return '(0%)';
                        }
                        const diff = townValue - ifmvValue;
                        if (Math.abs(diff) < ifmvValue * 0.001) {
                          return '(0%)';
                        }
                        const pct = Math.round(diff / ifmvValue * 100);
                        return `(${pct}%)`;
                      })() : '(0%)'}
                    </span>
                    {isTrainingUser && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={townOfferProdCheckbox}
                            onChange={handleProdCheckboxChange}
                            size="small"
                            sx={{
                              padding: '2px',
                              '& .MuiSvgIcon-root': {
                                fontSize: '14px',
                              },
                            }}
                          />
                        }
                        label="Prod"
                        sx={{
                          margin: 0,
                          marginLeft: '4px',
                          '& .MuiFormControlLabel-label': {
                            fontSize: '9px',
                            fontWeight: 'bold',
                          },
                        }}
                      />
                    )}
                  </div>
                  <div className="flex items-center">
                    <span className="text-[10px] text-right font-normal pr-1 flex items-center justify-end" style={{ marginTop: '-4px', width: '120px', minWidth: '120px', maxWidth: '120px' }}>
                      <SvgIconButton
                        icon={AutorenewIcon}
                        onClick={() => setIsAVMode((mode) => !mode)}
                        disabled={isSettled}
                        tooltip={isSettled ? "Case is Settled" : (isAVMode ? "Switch offer to Market Value" : "Switch offer to AV")}
                        className="p-0.5 mr-0.5"
                        size="small"
                        activeColor="text.secondary"
                        iconProps={{
                          sx: { fontSize: '12px' }
                        }}
                      />
                      <Tooltip title={isSettled ? "Case is Settled" : "Set town offer options"} disableInteractive>
                        <IconButton
                          size="small"
                          onClick={handleTownSetMenuOpen}
                          disabled={isSettled}
                          sx={{ 
                            padding: '0px 2px', 
                            marginTop: '2px',
                            minWidth: 'auto',
                            width: 'auto',
                            height: '12px',
                            border: '1px solid #666',
                            borderRadius: '2px',
                            backgroundColor: 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '2px'
                          }}
                        >
                          <span style={{ fontSize: '8px', fontWeight: 'bold', lineHeight: '1' }}>SET</span>
                        </IconButton>
                      </Tooltip>
                      <span className="flex-grow text-right">
                        {isAVMode ? 'MV' : 'AV'}: {parseNumericValue(effectiveTownOfferValue) !== null ? (isAVMode ? (() => {
                          const v = parseNumericValue(effectiveTownOfferValue);
                          const propertyAssessmentForCase = (currentCase?.PropertyAssessment || currentCase?.AssessmentVillage || 0);
                          const equalsAssessment = Math.abs((v || 0) - propertyAssessmentForCase) <= 1;
                          return (equalsAssessment ? Math.round(v / conversionFactor) : Math.round(v / conversionFactor / 10) * 10).toLocaleString();
                        })() : Math.round(parseNumericValue(effectiveTownOfferValue)).toLocaleString()) : 'None'}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
        </div>
        {(settledPerformance || settledPerformanceLoading) && (
          <Tooltip
            title={settledPerformanceLoading
              ? 'Updating resolved results'
              : (
                <div>
                  <div>
                    {`${resultTypeDisplayLabel(settledPerformance.resultType)} settled AV vs starting AV across ${settledPerformance.resolvedCases ?? 0} of ${settledPerformance.totalCases ?? 0} cases.`}
                  </div>
                  <div>{formatWinRateLine(settledPerformance.wins, settledPerformance.resolvedCases)}</div>
                  {priorYears.map((row) => (
                    <div key={row.year}>{row.year}: {formatPortfolioReduction(row.ratio)}</div>
                  ))}
                  {priorAvg != null && <div>2y avg: {formatPortfolioReduction(priorAvg)}</div>}
                  <div>
                    Current: {formatPortfolioReduction(settledPerformance.ratio)}
                    {priorDeltaPct != null && (
                      <span className={abovePrior ? 'text-red-300' : belowPrior ? 'text-green-300' : ''}>
                        {' '}({abovePrior ? '+' : ''}{priorDeltaPct.toFixed(1)}%)
                      </span>
                    )}
                  </div>
                </div>
              )}
            placement="top"
            disableInteractive
          >
            <div className="flex flex-col items-center justify-center border border-[#cfd5dd] bg-white rounded-md px-2" style={{ height: '38.5px' }}>
              <span className="text-[10px] font-normal whitespace-nowrap">
                Resolved{settledPerformance ? ` (${settledPerformance.resolvedCases ?? 0}/${settledPerformance.totalCases ?? 0})` : ''}
              </span>
              {settledPerformanceLoading ? (
                <span className="inline-flex items-center">
                  <CircularProgress size={12} thickness={5} />
                  <HelpOutlineIcon sx={{ fontSize: 13, color: '#999', ml: '2px' }} />
                </span>
              ) : (
                <span className={`text-sm inline-flex items-center whitespace-nowrap ${settledPerformance.ratio > 0 ? 'text-green-700' : 'text-red-700'}`}>
                  {formatPortfolioReduction(settledPerformance.ratio)}
                  {abovePrior && <ArrowUpwardIcon className="text-red-700 ml-0.5" sx={{ fontSize: 15 }} />}
                  {belowPrior && <ArrowDownwardIcon className="text-green-700 ml-0.5" sx={{ fontSize: 15 }} />}
                  <HelpOutlineIcon sx={{ fontSize: 13, color: '#999', ml: '2px' }} />
                </span>
              )}
            </div>
          </Tooltip>
        )}
      </div>
      
      {/* SET Menu Dropdown */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleSetMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {ifmv > 450000 && (
          <MenuItem onClick={handleSetMaxOffer} disabled={isSettled} sx={{ fontSize: '0.75rem', padding: '4px 8px' }}>
            <div className="flex items-center justify-between w-full">
              <span>Max</span>
              <Tooltip title={isSettled ? "Case is Settled" : "Set offer to 75% of IFMV (25% reduction)"} disableInteractive>
                <InfoOutlinedIcon sx={{ fontSize: '12px', marginLeft: '4px' }} />
              </Tooltip>
            </div>
          </MenuItem>
        )}
          <MenuItem onClick={handleSetRandomOffer} disabled={isSettled} sx={{ fontSize: '0.75rem', padding: '4px 8px' }}>
            <div className="flex items-center justify-between w-full">
              <span>Random</span>
              <Tooltip title={isSettled ? "Case is Settled" : "Set offer to random value between 97-94% of IFMV (3-6% reduction)"} disableInteractive>
                <InfoOutlinedIcon sx={{ fontSize: '12px', marginLeft: '4px' }} />
              </Tooltip>
            </div>
          </MenuItem>
        <MenuItem 
          onClick={handleSetWorkupAverageOffer} 
          disabled={isSettled || !isValidValue(getCurrentValue())} 
          sx={{ fontSize: '0.75rem', padding: '4px 8px' }}
        >
          <div className="flex items-center justify-between w-full">
            <span>Selected Average</span>
            <Tooltip title={isSettled ? "Case is Settled" : "Average price of selected comps"} disableInteractive>
              <InfoOutlinedIcon sx={{ fontSize: '12px', marginLeft: '4px' }} />
            </Tooltip>
          </div>
        </MenuItem>
        <MenuItem 
          onClick={handleSetSelectedMedianOffer} 
          disabled={isSettled || !isValidValue(compAvg.selectedMedian)} 
          sx={{ fontSize: '0.75rem', padding: '4px 8px' }}
        >
          <div className="flex items-center justify-between w-full">
            <span>Selected Median</span>
            <Tooltip title={isSettled ? "Case is Settled" : "Median price of selected comps"} disableInteractive>
              <InfoOutlinedIcon sx={{ fontSize: '12px', marginLeft: '4px' }} />
            </Tooltip>
          </div>
        </MenuItem>
        <MenuItem onClick={handleSetNoChangeOffer} disabled={isSettled} sx={{ fontSize: '0.75rem', padding: '4px 8px' }}>
          <div className="flex items-center justify-between w-full">
            <span>No Change</span>
            <Tooltip title={isSettled ? "Case is Settled" : "Set offer to IFMV rounded to nearest integer"} disableInteractive>
              <InfoOutlinedIcon sx={{ fontSize: '12px', marginLeft: '4px' }} />
            </Tooltip>
          </div>
        </MenuItem>
      </Menu>
      
      {/* Town Offer SET Menu Dropdown */}
      <Menu
        anchorEl={townAnchorEl}
        open={Boolean(townAnchorEl)}
        onClose={handleTownSetMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={handleSetNoChangeTownOffer} disabled={isSettled} sx={{ fontSize: '0.75rem', padding: '4px 8px' }}>
          <div className="flex items-center justify-between w-full">
            <span>No Change</span>
            <Tooltip title={isSettled ? "Case is Settled" : "Set town offer to IFMV rounded to nearest integer"} disableInteractive>
              <InfoOutlinedIcon sx={{ fontSize: '12px', marginLeft: '4px' }} />
            </Tooltip>
          </div>
        </MenuItem>
      </Menu>
      
      {/* Offer Round Confirmation Dialog */}
      <OfferRoundConfirmationDialog
        open={offerRoundDialogOpen}
        onClose={handleDialogClose}
        onConfirm={offerRoundDialogData?.onConfirm}
        onCancel={offerRoundDialogData?.onCancel}
        onEditPriorRound={offerRoundDialogData?.onEditPriorRound}
        parcelId={offerRoundDialogData?.parcelId}
        newRoundNumber={offerRoundDialogData?.newRoundNumber}
        negotiationRounds={offerRoundDialogData?.negotiationRounds}
        offeredBy={offerRoundDialogData?.offeredBy}
        newOfferValue={offerRoundDialogData?.newOfferValue}
      />

      {/* Production Checkbox Confirmation Dialog */}
      <StableMuiDialog
        open={prodCheckboxDialogOpen}
        onClose={handleProdCheckboxCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
          Production Mode Warning
        </DialogTitle>
        <DialogContent>
          <div style={{ fontSize: '0.95rem', lineHeight: '1.5' }}>
            This setting enables PRODUCTION offer updates for the town offer field. It should be off by default, unless you intentionally need to update production town offers.
          </div>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleProdCheckboxCancel}
            variant="outlined"
            sx={{ fontSize: '0.9rem' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleProdCheckboxConfirm}
            variant="contained"
            color="primary"
            sx={{ fontSize: '0.9rem' }}
          >
            Confirm
          </Button>
        </DialogActions>
      </StableMuiDialog>

    </div>
  );
};

export default React.memo(ToolbarValueDisplay); 